// Icons for the quotation editor's split-view control and its menu (#1311).
//
// `SplitViewIcon` is deliberately NOT the existing `SplitScreenIcon` (a single rect with a centre
// divider): the header control is two FACING brackets with a vertical bar between them. Its
// `active` variant fills the left bracket solid --clr-main, which is what makes the toggle's
// on-state readable at 28px.
//
// `SplitScreenIcon` is currently UNUSED. The design assigns that glyph to a row-level "Open in
// split view" button in the eref sidebar's Elevation-callouts list, which is not implemented and is
// not part of #1311; the icon is kept for it rather than deleted and redrawn later.

type Props = { className?: string }

const MENU_STROKE = {
	stroke: "currentColor",
	strokeWidth: 1.2,
	strokeLinecap: "round",
	strokeLinejoin: "round",
} as const

export const SplitViewIcon = ({ active = false }: { active?: boolean }) => (
	<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		{/* Left bracket — solid while split screen is on. */}
		<path
			d="M12.2 8H9.1A1.1 1.1 0 0 0 8 9.1v9.8A1.1 1.1 0 0 0 9.1 20h3.1"
			fill={active ? "var(--clr-main, #0047ff)" : "none"}
			stroke={active ? "var(--clr-main, #0047ff)" : "currentColor"}
			strokeWidth="1.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		{/* The divider between the two views. */}
		<path
			d="M14 7v14"
			stroke={active ? "var(--clr-main, #0047ff)" : "currentColor"}
			strokeWidth="1.2"
			strokeLinecap="round"
		/>
		{/* Right bracket — always outlined. */}
		<path
			d="M15.8 8h3.1A1.1 1.1 0 0 1 20 9.1v9.8A1.1 1.1 0 0 1 18.9 20h-3.1"
			stroke={active ? "var(--clr-main, #0047ff)" : "currentColor"}
			strokeWidth="1.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

export const CloseLeftViewIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<rect x="1.4" y="2.6" width="13.2" height="10.8" rx="1.2" {...MENU_STROKE} />
		<path d="M9.6 8H5.2m1.9-1.9L5.2 8l1.9 1.9" {...MENU_STROKE} />
	</svg>
)

export const CloseRightViewIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<rect x="1.4" y="2.6" width="13.2" height="10.8" rx="1.2" {...MENU_STROKE} />
		<path d="M6.4 8h4.4M8.9 6.1 10.8 8 8.9 9.9" {...MENU_STROKE} />
	</svg>
)

export const ReverseViewIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<rect x="1.4" y="2.6" width="13.2" height="10.8" rx="1.2" {...MENU_STROKE} />
		<path d="M8 2.6v10.8" {...MENU_STROKE} />
		<path d="M8.9 3.5h4.8a.9.9 0 0 1 .9.9v7.2a.9.9 0 0 1-.9.9H8.9V3.5Z" fill="currentColor" />
	</svg>
)

export const AutoOpenFloorPlanIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<path d="M8 2.6H2.6a1.2 1.2 0 0 0-1.2 1.2v8.4a1.2 1.2 0 0 0 1.2 1.2H8" {...MENU_STROKE} />
		<path d="M11.2 2.6h1.2a1.2 1.2 0 0 1 1.2 1.2v.9m0 3v1.6m0 3v.9a1.2 1.2 0 0 1-1.2 1.2h-1.2" {...MENU_STROKE} />
	</svg>
)

// "Dual view in new tab" (#1430): a window frame with an arrow leaving it — the view sent out to
// its own OS window (and, with two monitors, to the other screen). The PoC drew this on the 28x28
// toolbar grid; the feature is a MENU ROW now, so it is redrawn at 16x16 on the 1.2 stroke its
// siblings above use, and inherits `currentColor` so a disabled row greys the icon with its label.
export const PopOutViewIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<path d="M7.4 3.2H3.2a1.2 1.2 0 0 0-1.2 1.2v7.4A1.2 1.2 0 0 0 3.2 13h7.4a1.2 1.2 0 0 0 1.2-1.2V7.6" {...MENU_STROKE} />
		<path d="M9.4 2.4H14v4.6" {...MENU_STROKE} />
		<path d="M14 2.4 8.2 8.2" {...MENU_STROKE} />
	</svg>
)

// "Return view back" / "Return right view back" (#1430): the same window frame with the arrow
// coming IN — the mirror of PopOutViewIcon, so the two rows read as one round trip.
export const ReturnViewBackIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<path d="M7.4 3.2H3.2a1.2 1.2 0 0 0-1.2 1.2v7.4A1.2 1.2 0 0 0 3.2 13h7.4a1.2 1.2 0 0 0 1.2-1.2V7.6" {...MENU_STROKE} />
		<path d="M13.4 7.4V2.8H8.8" {...MENU_STROKE} />
		<path d="M8.2 8.2 13.4 3" {...MENU_STROKE} />
	</svg>
)

export const SynchronizeScrollingIcon = ({ className }: Props) => (
	<svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<path d="M2.4 8a5.6 5.6 0 0 1 9.4-4.1l1.8 1.6" {...MENU_STROKE} />
		<path d="M13.6 8a5.6 5.6 0 0 1-9.4 4.1l-1.8-1.6" {...MENU_STROKE} />
		<path d="M13.6 2.6v2.9h-2.9M2.4 13.4v-2.9h2.9" {...MENU_STROKE} />
	</svg>
)
